import type { EntityFieldConfig, QueryFilterField, QueryValue, SingleKeyField } from './types'

const parsePositiveInteger = (raw: unknown): number | undefined => {
  if (typeof raw !== 'string' || !/^[1-9]\d*$/.test(raw)) {
    return undefined
  }
  const parsed = Number(raw)
  return Number.isSafeInteger(parsed) ? parsed : undefined
}

const firstString = (raw: QueryValue): string | undefined => {
  if (raw === undefined || raw === null) {
    return undefined
  }
  if (Array.isArray(raw)) {
    const first = raw[0]
    return typeof first === 'string' ? first : undefined
  }
  return typeof raw === 'string' ? raw : undefined
}

const makeSingleKeyField = <T>({
  key,
  parse,
  toRaw,
  isDefault,
}: SingleKeyField<T>): QueryFilterField<T> => ({
  parse: (raw) => parse(raw),
  serialize: (value, query) => {
    if (isDefault(value)) {
      delete query[key]
      return
    }
    const raw = toRaw(value)
    if (raw === undefined) {
      delete query[key]
    } else {
      query[key] = raw
    }
  },
})

export const stringField = (
  key: string,
  defaultValue?: string,
): QueryFilterField<string | undefined> =>
  makeSingleKeyField<string | undefined>({
    key,
    parse: (raw) => firstString(raw) ?? defaultValue,
    toRaw: (value) => value,
    isDefault: (value) => value === defaultValue || value === undefined || value === '',
  })

export const numberField = (
  key: string,
  defaultValue: number,
  options: { min?: number; max?: number } = {},
): QueryFilterField<number> =>
  makeSingleKeyField<number>({
    key,
    parse: (raw) => {
      const str = firstString(raw)
      if (str === undefined) {
        return defaultValue
      }
      const parsed = Number.parseInt(str, 10)
      if (!Number.isFinite(parsed)) {
        return defaultValue
      }
      if (options.min !== undefined && parsed < options.min) {
        return defaultValue
      }
      if (options.max !== undefined && parsed > options.max) {
        return defaultValue
      }
      return parsed
    },
    toRaw: (value) => String(value),
    isDefault: (value) => value === defaultValue,
  })

export const numberEntityArrayField = (
  idKey: string,
  nameKey: string,
): QueryFilterField<{ id: number; name: string }[] | undefined> => ({
  parse: (_, query) => {
    const idsRaw = query[idKey]
    const namesRaw = query[nameKey]
    if (idsRaw === undefined || idsRaw === null) {
      return undefined
    }
    const ids = Array.isArray(idsRaw) ? idsRaw : [idsRaw]
    const names =
      namesRaw === undefined || namesRaw === null
        ? []
        : Array.isArray(namesRaw)
          ? namesRaw
          : [namesRaw]

    return ids.flatMap((rawId, index) => {
      const name = names[index]
      const id = parsePositiveInteger(rawId)
      return id === undefined || typeof name !== 'string' || name.length === 0 ? [] : [{ id, name }]
    })
  },
  serialize: (value, query) => {
    if (!value || value.length === 0) {
      delete query[idKey]
      delete query[nameKey]
      return
    }
    query[idKey] = value.map(({ id }) => String(id))
    query[nameKey] = value.map(({ name }) => name)
  },
})

export const booleanField = (key: string): QueryFilterField<boolean> =>
  makeSingleKeyField<boolean>({
    key,
    parse: (raw) =>
      firstString(raw) === 'true' || raw === '' || (raw !== undefined && firstString(raw) === '1'),
    toRaw: (value) => (value ? 'true' : undefined),
    isDefault: (value) => !value,
  })

export const enumField = <E extends string>(
  key: string,
  allowed: readonly E[],
  defaultValue?: E,
): QueryFilterField<E | undefined> =>
  makeSingleKeyField<E | undefined>({
    key,
    parse: (raw) => {
      const str = firstString(raw)
      return str !== undefined && (allowed as readonly string[]).includes(str)
        ? (str as E)
        : defaultValue
    },
    toRaw: (value) => value,
    isDefault: (value) => value === defaultValue || value === undefined,
  })

export const stringArrayField = (
  key: string,
  defaultValue: string[] = [],
): QueryFilterField<string[] | undefined> => ({
  parse: (raw) => {
    if (raw === undefined || raw === null) {
      return defaultValue.length > 0 ? [...defaultValue] : undefined
    }
    const arr = Array.isArray(raw) ? raw : [raw]
    return arr.filter((item): item is string => typeof item === 'string')
  },
  serialize: (value, query) => {
    if (!value || value.length === 0) {
      delete query[key]
      return
    }
    if (
      value.length === defaultValue.length &&
      value.every((item, index) => item === defaultValue[index])
    ) {
      delete query[key]
      return
    }
    query[key] = value
  },
})

export const enumArrayField = <E extends string>(
  key: string,
  allowed: readonly E[],
  defaultValue: E[] = [],
): QueryFilterField<E[] | undefined> => {
  const allowedSet = new Set<string>(allowed)
  return {
    parse: (raw) => {
      if (raw === undefined || raw === null) {
        return defaultValue.length > 0 ? [...defaultValue] : undefined
      }
      const arr = Array.isArray(raw) ? raw : [raw]
      return arr.filter((item): item is E => typeof item === 'string' && allowedSet.has(item))
    },
    serialize: (value, query) => {
      if (!value || value.length === 0) {
        delete query[key]
        return
      }
      if (
        value.length === defaultValue.length &&
        value.every((item, index) => item === defaultValue[index])
      ) {
        delete query[key]
        return
      }
      query[key] = value
    },
  }
}

const buildEntityField = <E extends { id: number | string }>(
  config: EntityFieldConfig<E>,
  parseId: (raw: string) => E['id'] | undefined,
): QueryFilterField<E | undefined> => {
  const { idKey, labelKey, entityLabelKey } = config
  return {
    parse: (_, query) => {
      const idStr = firstString(query[idKey])
      if (idStr === undefined) {
        return undefined
      }
      const id = parseId(idStr)
      if (id === undefined) {
        return undefined
      }
      const entity: Record<string, unknown> = { id }
      if (labelKey && entityLabelKey) {
        const labelStr = firstString(query[labelKey])
        entity[entityLabelKey as string] = labelStr ?? ''
      }
      return entity as E
    },
    serialize: (value, query) => {
      if (!value) {
        delete query[idKey]
        if (labelKey) {
          delete query[labelKey]
        }
        return
      }
      query[idKey] = String(value.id)
      if (labelKey && entityLabelKey) {
        const label = (value as Record<string, unknown>)[entityLabelKey as string]
        if (typeof label === 'string' && label.length > 0) {
          query[labelKey] = label
        } else {
          delete query[labelKey]
        }
      }
    },
  }
}

export const entityField = <E extends { id: number }>(
  config: EntityFieldConfig<E>,
): QueryFilterField<E | undefined> =>
  buildEntityField(config, (raw) => {
    const id = Number.parseInt(raw, 10)
    return Number.isFinite(id) ? id : undefined
  })

/** Same URL shape as {@link entityField}, for entities keyed by a string id such as a UUID. */
export const stringEntityField = <E extends { id: string }>(
  config: EntityFieldConfig<E>,
): QueryFilterField<E | undefined> => buildEntityField(config, (raw) => raw || undefined)
